// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as Cookie from 'app/packages/cookie-utils';
import CONST from 'app/common/lib/Const';
import CONTAINER_NAME from 'app/common/react/components/growth/unauth/lib/loggingContainerConstants';
import googleOneTapFailed from 'app/www-unauth/redux/actions/googleOneTapActions';
import googleOneTapUtil from 'app/common/react/components/growth/unauth/signup/GoogleOneTap/googleOneTapUtil';
import i18n from 'app/common/lib/i18n';
import loadScript from 'app/packages/script-loader';
import login from 'app/common/react/components/growth/unauth/signup/UserLogin';
import loginUtil from 'app/www-unauth/lib/loginUtil';
import PropTypes from 'prop-types';
import SuspendedModal from 'app/common/react/components/growth/unauth/signup/AccountRecovery/SuspendedModal';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import type { SignupCredentialsAndLoggingData } from 'app/packages/pinterest-flow-types/registration';
import type { State } from 'app/common/redux/types';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import UNAUTH_PAGE_TYPE from 'app/common/react/components/growth/unauth/lib/UnauthPageType';
import UserRegistration from 'app/common/react/components/growth/unauth/signup/UserRegistration';
import { doesIOv2Exist } from 'app/packages/browser';
import { showError } from 'app/common/react/components/growth/unauth/lib/ErrorUtils';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import { track } from 'app/common/lib/react/analytics';
import GoogleOneTapModal, {
  CONFIRMATION_DIALOG_STEP,
} from 'app/common/react/components/growth/unauth/signup/GoogleOneTap/GoogleOneTapModal';
import type {
  GoogleOneTapLoginCreds,
  GoogleOneTapActivity,
  GoogleOneTapCredentialResponse,
} from 'app/packages/pinterest-flow-types/authentication';
import withUnauthSeoExperiments, {
  type UnauthSeoExperiments,
} from 'app/common/lib/react/withUnauthSeoExperiments';
import {
  CREDENTIAL_REQUEST_ERROR,
  dismissIframe,
  retrieveCredentials,
  closeOneTap,
} from 'app/packages/google-one-tap';
import {
  registrationRequiresAgeGDPR,
  SIGNUP_METHOD,
} from 'app/packages/pinterest-authentication/registration';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { compose, type Dispatch } from 'redux';

const SIGNUP_CONFIRMATION_SPINNER_DELAY = 2; // seconds
const SMARTLOCK_REQUEST_TYPE = Object.freeze({
  RETRIEVE: 'retrieve', // automatically retrieve credentials without user interaction
  HINT: 'hint', // retrieve credentials with user interaction
});
const EN_COUNTRIES_WITH_MANDATORY_AGE = ['US', 'CA', 'NZ', 'AU'];

type OwnProps = {|
  container?: string,
  handleSignupConfirmed?: (
    method: string,
    userData: {},
    credentials: SignupCredentialsAndLoggingData,
  ) => void,
  handle2FALogin: (creds: GoogleOneTapLoginCreds | { gplus_id_token: string }) => void,
  handleEmptyCredentials: () => void,
  handleLoginError: (error: Object) => void,
  handleSignupError: () => void,
  handleShowLogin: () => void,
  nextUrl?: string,
  reportFailureToShow: () => void,
  showRegisterLoadingScreen?: (boolean) => void,
  shouldHide?: boolean,
|};

type ReduxState = {|
  viewer: Viewer,
|};

type GoogleOneTapProps = {|
  ...OwnProps,
  ...ReduxState,
  dispatch: Dispatch<any>,
  requestContext: RequestContextType,
  seoUnauthExperiments: UnauthSeoExperiments,
|};

type GoogleOneTapState = {|
  confirmationDialogStep?:
    | 'delayedSignupLoadingSpinner'
    | 'staticSignupLogin'
    | 'staticSignupLoginWithTOS',
  googleOpenId: string,
  loading: boolean,
  showAccountSuspended: boolean,
|};

type Context = {|
  country: string,
  isBot?: boolean,
  userAgent: {
    isMobile: boolean,
  },
  hybridTier: string,
  pageType?: string,
|};

export class GoogleOneTap extends Component<GoogleOneTapProps, GoogleOneTapState> {
  constructor(props: GoogleOneTapProps) {
    super(props);
    this.state = {
      confirmationDialogStep: undefined,
      googleOpenId: '',
      loading: false,
      showAccountSuspended: false,
    };
  }

  componentDidMount() {
    if (
      !googleOneTapUtil.isGoogleOneTapDisabled() ||
      (!this.context.isBot && Cookie.get(PCONST.COOKIE_FB_AUTO_LOGIN_DISABLED))
    ) {
      if (this.inMigrationExperiment()) {
        this.setupGoogleOneTapV2();
      } else {
        this.setupGoogleOneTap();
      }

      if (!this.context.isBot) {
        trackRegisterAction(
          `unauth_web_container.${CONTAINER_NAME.GOOGLE_ONE_TAP}.${
            this.context.hybridTier
          }.other.shown`,
        );
      }
    }
  }

  componentDidUpdate(prevProps: GoogleOneTapProps) {
    if (this.props.shouldHide && !prevProps.shouldHide) {
      this.hide();
    }
  }

  // =================== logic for One Tap migration ===================

  inMigrationExperiment() {
    const { requestContext } = this.props;
    const experimentName = doesIOv2Exist()
      ? 'web_unauth_one_tap_migration_iov2_available'
      : 'web_unauth_one_tap_migration_iov2_unavailable';
    return new SingleTreatmentExp(requestContext, experimentName).enabled();
  }

  setupGoogleOneTapV2() {
    loadScript('https://accounts.google.com/gsi/client').then(() => {
      this.trackGoogleOneTapAction('gsi_script_loaded');
      const google = window.google;
      if (google && google.accounts && google.accounts.id) {
        this.trackGoogleOneTapAction('sdk_exists');
        const oneTap = google.accounts.id;
        oneTap.initialize({
          client_id: CONST.GEN.templateConst.settings.GPLUS_CLIENT_ID,
          // only auto_select if they didn't logout recently
          auto_select: !Cookie.get(PCONST.COOKIE_FB_AUTO_LOGIN_DISABLED),
          callback: this.handleOneTapResponse.bind(this),
          cancel_on_tap_outside: false,
          context: 'use',
          activity_listener: this.handleActivity.bind(this),
        });

        this.trackGoogleOneTapAction('initialize');

        oneTap.prompt();
      }
    });
  }

  handleOneTapResponse(response: GoogleOneTapCredentialResponse) {
    if (response) {
      this.trackGoogleOneTapAction(`select_by.${response.select_by}`);

      // TODO (@thakomori): hint and retrieve will no longer exist after the One Tap migration,
      // so that distinction will no longer be useful. Once the migration ships, refactor
      // onRetrieveCredentialSuccess and subsequent downstream methods to no longer make use of that
      // distinction.
      const requestType =
        response.select_by === 'auto'
          ? SMARTLOCK_REQUEST_TYPE.RETRIEVE
          : SMARTLOCK_REQUEST_TYPE.HINT;
      this.onRetrieveCredentialsSuccess({ idToken: response.credential }, requestType);
    }
  }

  handleActivity(activity: GoogleOneTapActivity) {
    if (activity) {
      if (activity.type === 'error') {
        this.trackGoogleOneTapAction(`error.${activity.errorType}`);
        this.handleEmptyCredentials();
        this.props.dispatch(googleOneTapFailed());
        return;
      }

      // type will be {ui_change | user_action}, whereas activityType describes the actual activity
      const activityType =
        activity.type === 'ui_change' ? activity.uiActivityType : activity.userActivityType;

      // log all activity so we have visibility into the user funnel
      this.trackGoogleOneTapAction(`activity.${activity.type}.${activityType}`);

      if (activityType === 'close_prompt') {
        this.handleEmptyCredentials();
      }
    }
  }

  // =================== /end logic for One Tap migration ===================

  componentWillUnmount() {
    dismissIframe(window.smartlock);

    closeOneTap(window.google);
  }

  handleEmptyCredentials() {
    if (this.props.handleEmptyCredentials) {
      this.props.handleEmptyCredentials();
    }
  }

  onRetrieveCredentialsSuccess = (hint: { idToken: string }, requestType: string) => {
    googleOneTapUtil.disableGoogleOneTapDialog();
    this.showLoading();
    this.trackGoogleOneTapAction(`${requestType}.success`);

    const loginCreds = {
      google_open_id_token: hint.idToken,
    };

    // Check if the user has succesfully signed in to gplus then log the user into Pinterest.
    this.login(loginCreds).then(
      (response) => {
        this.onLoginSuccess(response);
      },
      (error) => {
        this.onLoginFailure(loginCreds, requestType, error);
      },
    );
    this.trackSignupLoginEvent(PCONST.thrift.EventType.LOGIN_ATTEMPT);
  };

  onRetrieveCredentialsFailure = (error: { type: string }, requestType: string) => {
    const errorType = (error && error.type) || 'unknown';
    this.trackGoogleOneTapAction(`${requestType}.error.${errorType}`);
    if (
      (!error.type || error.type !== CREDENTIAL_REQUEST_ERROR.USER_CANCELED) &&
      this.props.reportFailureToShow
    ) {
      this.props.reportFailureToShow();
    }
  };

  onLoginSuccess = (loginResponse: Object = {}) => {
    const { nextUrl } = this.props;
    this.trackGoogleOneTapAction('hint.login.success');
    this.trackSignupLoginEvent(PCONST.thrift.EventType.LOGIN_SUCCESS);
    if (nextUrl) {
      loginUtil.handleRedirect(nextUrl);
    } else {
      loginUtil.handleRedirectWithFallbackWindowLocation();
    }
  };

  onLoginFailure = (
    creds: GoogleOneTapLoginCreds,
    requestType: string,
    error: { api_error_code: string },
  ) => {
    const { handleLoginError, handle2FALogin } = this.props;
    this.hideLoading();
    const apiErrorCode = (error && error.api_error_code) || 0; // 0 is an unused default

    this.trackGoogleOneTapAction(`hint.login.error.${apiErrorCode}`);
    this.trackSignupLoginEvent(PCONST.thrift.EventType.LOGIN_FAILURE);
    if (apiErrorCode === CONST.LOGIN_USER_NOT_FOUND) {
      this.showConfirmationDialog(creds, requestType);
    } else if (apiErrorCode === CONST.LOGIN_MFA_REQUIRED) {
      // 2FA login interprets google_open_id_token as a gplus_id_token
      handle2FALogin({ gplus_id_token: creds.google_open_id_token });
    } else if (apiErrorCode === CONST.POLICY_VIOLATION_USER) {
      this.setState({ showAccountSuspended: true });
    } else {
      const errorText = i18n._('We couldn’t log you in with that account. Try the Google button!');

      showError(errorText, null);
      handleLoginError(error);
    }
  };

  onSignupSuccess = (response?: Object) => {
    this.trackGoogleOneTapAction('hint.signup.success');
    this.trackSignupLoginEvent(PCONST.thrift.EventType.SIGNUP_SUCCESS);
    const { pageType } = this.context;
    const { requestContext } = this.props;
    const pinLPNoRedirectExp = new SingleTreatmentExp(
      requestContext,
      'web_unauth_got_signup_no_redirect_plp',
    );
    const boardLPNoRedirectExp = new SingleTreatmentExp(
      requestContext,
      'web_unauth_got_signup_no_redirect_blp',
    );
    if (
      (pageType === UNAUTH_PAGE_TYPE.PIN && pinLPNoRedirectExp.enabled()) ||
      (pageType === UNAUTH_PAGE_TYPE.BOARD && boardLPNoRedirectExp.enabled())
    ) {
      // keep user on current page
      loginUtil.handleSignupRedirect(loginUtil.defaultLoginRedirectUrl());
    } else {
      loginUtil.handleSignupRedirect('/');
    }
  };

  onSignupFailure = (error: { api_error_code: string }) => {
    const {
      userAgent: { isMobile },
    } = this.context;
    const apiErrorCode = (error && error.api_error_code) || 0; // 0 is an unused default
    this.trackGoogleOneTapAction(`hint.signup.error.${apiErrorCode}`);
    this.trackSignupLoginEvent(PCONST.thrift.EventType.SIGNUP_FAILURE);
    this.hideLoading();
    this.hideConfirmationDialog();

    let errorHref = null;
    let errorText = '';
    if (isMobile) {
      errorText = i18n._(
        'Oops! We couldn’t sign you up with that account. Try again from our home page.',
      );
      errorHref = '/login/';
    } else {
      errorText = i18n._('We couldn’t sign you up with that account. Try the Google button!');
    }

    showError(errorText, errorHref);
    this.props.handleSignupError();
  };

  setupGoogleOneTap() {
    // Do not atempt to setup Google One-Tap if the page was renderd by a bot.
    if (this.context.isBot) {
      return;
    }

    window.onSmartLockLoad = (smartlock) => {
      if (this.props.shouldHide) {
        return;
      }
      this.trackGoogleOneTapAction('setup.attempt');
      try {
        // Openyolo: Object for a new standard for credential exchange in web browser
        // https://github.com/openid/OpenYOLO-Web
        const openyolo: Object = window.openyolo;
        this.trackGoogleOneTapAction(`open_yolo_load.${String(!!openyolo)}`);

        openyolo.setProviderUrlBase('https://smartlock.google.com/iframe/request');
        if (Cookie.get(PCONST.COOKIE_FB_AUTO_LOGIN_DISABLED)) {
          this.trackGoogleOneTapAction('show_google_one_tap_in_log_out');
          this.attemptCredentialAuthentication(smartlock, SMARTLOCK_REQUEST_TYPE.HINT, true);
        } else {
          this.attemptCredentialAuthentication(smartlock, SMARTLOCK_REQUEST_TYPE.RETRIEVE, false);
        }
      } catch (error) {
        const errorName = (error && error.name) || 'unknown';
        this.trackGoogleOneTapAction(`setup.error.${errorName}`);
        this.handleEmptyCredentials();
      }
    };

    this.addGoogleOneTapScript();
  }

  hide() {
    this.hideConfirmationDialog();
    dismissIframe(window.smartlock);
  }

  context: Context;

  trackSignupLoginEvent(event: string) {
    const { viewer } = this.props;
    if (viewer.isAuth) {
      track(event, {
        login_state: viewer.isLimitedLogin
          ? PCONST.LoginStates.LIMITED
          : PCONST.LoginStates.DEFAULT,
        signup_login_method: PCONST.SignupLoginMethods.GOOGLE_ONE_TAP,
      });
    } else {
      track(event, {
        signup_login_method: PCONST.SignupLoginMethods.GOOGLE_ONE_TAP,
      });
    }
  }

  trackGoogleOneTapAction = (action: string) => {
    trackRegisterAction(`unauth.google_one_tap.${action}`);
  };

  shouldUseMediatedAuthentication = () => !googleOneTapUtil.isGoogleOneTapDisabled();

  attemptCredentialAuthentication = (smartlock: any, requestType: string, mediated: boolean) => {
    this.trackGoogleOneTapAction(`request_credentials.${requestType}`);
    // HACK. setTimeout with a 0ms delay.
    // Sometimes smartlock is not available upon onSmartlockLockLoad being called, so we must re-queue
    // this task to the end of the event loop, where smartlock is guaranteed to be set.
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/EventLoop
    setTimeout(() => {
      const credentialsPromise = retrieveCredentials(smartlock, mediated);
      if (credentialsPromise !== null) {
        credentialsPromise
          .then((hint) => {
            this.onRetrieveCredentialsSuccess(hint, requestType);
          })
          .catch((error) => {
            this.onRetrieveCredentialsFailure(error, requestType);

            const errorType = (error && error.type) || '';

            // Terminate credential retrieval after a mediated attempt or if user dismissed the first prompt
            if (mediated || errorType === CREDENTIAL_REQUEST_ERROR.USER_CANCELED) {
              this.handleEmptyCredentials();
              if (mediated && errorType !== CREDENTIAL_REQUEST_ERROR.USER_CANCELED) {
                this.props.dispatch(googleOneTapFailed());
              }
              return;
            } else if (errorType === CREDENTIAL_REQUEST_ERROR.OPERATION_CANCELED) {
              return; // iframe was automatically dismissed
            }

            // Fallback to mediated credential retrieve if they have not previously interacted with Google One Tap
            if (this.shouldUseMediatedAuthentication()) {
              this.attemptCredentialAuthentication(smartlock, SMARTLOCK_REQUEST_TYPE.HINT, true);
            } else {
              this.handleEmptyCredentials();
            }
          });
      }
    }, 0);
  };

  base64urlUnescape(str: string) {
    return str.replace(/\-/g, '+').replace(/_/g, '/');
  }

  decodeToken(openIdToken: string) {
    // decoding a base 64 string back to the original string, source code from here:
    // https://developer.mozilla.org/en-US/docs/Web/API/WindowBase64/Base64_encoding_and_decoding#The_Unicode_Problem
    return JSON.parse(
      decodeURIComponent(
        atob(this.base64urlUnescape(openIdToken.split('.')[1]))
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      ),
    );
  }

  login(creds: GoogleOneTapLoginCreds) {
    const { seoUnauthExperiments } = this.props;
    const loggingData = {
      container: CONTAINER_NAME.GOOGLE_ONE_TAP,
      hybridTier: this.context.hybridTier,
      page: this.context.pageType,
    };
    return login(creds, loggingData, seoUnauthExperiments);
  }

  showConfirmationDialog(creds: GoogleOneTapLoginCreds, requestType: string) {
    const { container, seoUnauthExperiments } = this.props;
    let step = '';
    if (container === 'InspiredWallSignup') {
      step = CONFIRMATION_DIALOG_STEP.STATIC_SIGNUP_LOGIN;
    } else if (container === 'login_page') {
      step = CONFIRMATION_DIALOG_STEP.STATIC_SIGNUP_LOGIN_WITH_TOS;
    } else if (requestType === SMARTLOCK_REQUEST_TYPE.RETRIEVE) {
      if (
        (
          seoUnauthExperiments.v2ActivateExperiment(
            'unauth_desktop_force_tos_pp_on_static_confirmation_dialog',
          ) || ''
        ).startsWith('enabled')
      ) {
        step = CONFIRMATION_DIALOG_STEP.STATIC_SIGNUP_LOGIN_WITH_TOS;
      } else {
        step = CONFIRMATION_DIALOG_STEP.STATIC_SIGNUP_LOGIN;
      }
    } else {
      step = CONFIRMATION_DIALOG_STEP.DELAYED_SIGNUP_LOADING_SPINNER;
    }

    this.setState({ googleOpenId: creds.google_open_id_token, confirmationDialogStep: step });
  }

  hideConfirmationDialog() {
    this.setState({ confirmationDialogStep: undefined });
  }

  showLoading() {
    this.setState({ loading: true });
  }

  hideLoading() {
    this.setState({ loading: false });
  }

  handleCancelOneTap = () => {
    this.hideConfirmationDialog();
  };

  addGoogleOneTapScript() {
    const script = document.createElement('script');
    script.src = `https://smartlock.google.com/client?clientId=${
      CONST.GEN.templateConst.settings.GPLUS_CLIENT_ID
    }`;
    const querySelector = document.querySelector('head');
    if (querySelector !== null) {
      querySelector.appendChild(script);
    }
  }

  inMandatoryAgeStepENCountriesExperiment = () => {
    const { seoUnauthExperiments } = this.props;
    const { country } = this.context;
    return (
      EN_COUNTRIES_WITH_MANDATORY_AGE.includes(country) &&
      (
        seoUnauthExperiments.v2ActivateExperiment('web_unauth_mandatory_age_step_got_en') || ''
      ).startsWith('enabled')
    );
  };

  attemptSignup = (showLoading: boolean) => {
    const { country } = this.context;
    const { handleSignupConfirmed, requestContext, showRegisterLoadingScreen } = this.props;
    if (showLoading) {
      this.showLoading();
    }
    const { googleOpenId } = this.state;
    const decoded_hint = this.decodeToken(googleOpenId);
    const credentials = {
      google_open_id_token: googleOpenId,
      first_name: decoded_hint.given_name,
      last_name: decoded_hint.family_name,
      image_url: decoded_hint.picture,
      container: CONTAINER_NAME.GOOGLE_ONE_TAP,
      hybridTier: this.context.hybridTier,
      page: this.context.pageType,
    };

    if (
      handleSignupConfirmed &&
      (registrationRequiresAgeGDPR(country) || this.inMandatoryAgeStepENCountriesExperiment())
    ) {
      handleSignupConfirmed(SIGNUP_METHOD.GOOGLE_ONE_TAP, {}, credentials);
      return;
    }

    // todo(sophia) We need to flush experiment triggering immediately (current experiment client default wraps
    // activate resource call in a queued resource call with 5 second delay). Longterm soln is to support immediate
    // flush in ExperimentChecker class.
    // https://paper.dropbox.com/doc/Web-Welcome-Loading-Screen-Experiments-Triggering-Issues--AMlvl51RMVYopimH6DjeJ8EzAg-BwroITTNabE6ckNqNRoqJ
    const inWelcomeLoadingExp = new SingleTreatmentExp(
      requestContext,
      'web_unauth_google_one_tap_welcome',
    ).enabled();
    requestContext.experiments.flush();
    if (showRegisterLoadingScreen && inWelcomeLoadingExp) {
      showRegisterLoadingScreen(true);
    }

    UserRegistration.register(credentials).then(this.onSignupSuccess, this.onSignupFailure);
    this.trackSignupLoginEvent(PCONST.thrift.EventType.SIGNUP_ATTEMPT);
  };

  renderGoogleConfirmationDialog = (
    layoutStyle: 'top' | 'bottom',
    confirmationDialogStep:
      | 'delayedSignupLoadingSpinner'
      | 'staticSignupLogin'
      | 'staticSignupLoginWithTOS',
  ) => (
    <GoogleOneTapModal
      handleCancelClick={this.handleCancelOneTap}
      handleConfirmSignup={this.attemptSignup}
      handleShowLogin={this.props.handleShowLogin}
      layoutStyle={layoutStyle}
      loading={this.state.loading}
      step={confirmationDialogStep}
      delayedSignupLoadingSpinnerDuration={SIGNUP_CONFIRMATION_SPINNER_DELAY}
      shouldHide={this.props.shouldHide}
    />
  );

  render() {
    const {
      isBot,
      userAgent: { isMobile },
    } = this.context;

    const { showAccountSuspended } = this.state;
    // Google One-Tap is not supported for bots
    if (isBot) {
      return null;
    }

    if (showAccountSuspended) {
      return <SuspendedModal onClose={() => this.setState({ showAccountSuspended: false })} />;
    }

    // Expand triggering to earlier, since there's an imbalance triggering in attemptSignup
    this.inMandatoryAgeStepENCountriesExperiment();

    const { confirmationDialogStep } = this.state;

    if (confirmationDialogStep) {
      const layoutStyle = isMobile ? 'bottom' : 'top';
      return this.renderGoogleConfirmationDialog(layoutStyle, confirmationDialogStep);
    }

    return null;
  }
}

const mapStateToProps = (state: State): ReduxState => ({
  viewer: state.viewer,
});

GoogleOneTap.contextTypes = {
  country: PropTypes.string,
  isBot: PropTypes.bool,
  userAgent: PropTypes.shape({
    isMobile: PropTypes.bool.isRequired,
  }).isRequired,
  hybridTier: PropTypes.string,
  pageType: PropTypes.string,
};

export default compose(
  withRequestContext,
  withUnauthSeoExperiments,
  connect(mapStateToProps),
)(GoogleOneTap);
